import React, {useRef, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import PhoneVerificationView from '../../../components/PhoneOrEmailVerification/PhoneVerificationView';
import {
  RESEND_ERROR_MESSAGE,
  slowDown,
} from '../../../components/PhoneOrEmailVerification/slowDown';
import Otp from '../../../components/ui-controls/form/Otp';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {otp, space} from '../../../design-system/tokens';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import * as RootNavigation from '../../../navigation/RootNavigation';
import {useStores} from '../../../stores/use-stores';
import apiService from '../../../transport/apiService';
import {formatMobileForDisplay} from '../../../utils/phoneNumber';
import {OtpVerificationFormData, otpVerificationSchema} from '../../account/schemas/authSchemas';
import {useWideLayout} from '../../account/signUp/SignUpPageLayout';
import {goToActivateSuccess} from '../resetToHome';

// Desktop card: Figma 536:4581's fixed 49 x 52 boxes, as RewardsVerificationScreen.
const otpBoxSize = {width: otp.boxWidth, height: otp.boxHeight};

/**
 * Timeout for sending the code
 */
const TIMEOUT = 30000;

const RewardsActivateVerificationScreen = observer(
  ({
    route: _route,
    navigation,
  }: RouteNavigatorScreenProps<AppRoute.REWARDS_ACTIVATE_VERIFICATION>) => {
    const {userStore, rewardsStore, cartStore, shopSelectionStore} = useStores();

    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const [resendError, setResendError] = useState<string | undefined>(undefined);
    // Bumped to abandon an in-flight resend; a call whose id no longer matches
    // must not touch state.
    const resendReqIdRef = useRef(0);

    const phoneNumber = userStore.phoneNumber || '';

    const {
      control,
      handleSubmit,
      formState: {errors, isSubmitting},
      setError,
      reset,
    } = useForm<OtpVerificationFormData>({
      resolver: zodResolver(otpVerificationSchema),
      defaultValues: {verification: ''},
      mode: 'onTouched',
    });

    // DIS-1824: the same rules as the other mobile verification screens.
    // VERIFY stays disabled below 6 digits, and an error shows only at 6
    // digits. At 6 digits the zod schema passes, so any error there is the one
    // onSubmit set after a send. A part-typed code is never marked invalid.
    const verificationValue = useWatch({control, name: 'verification'}) ?? '';
    const codeComplete = verificationValue.length === 6;
    const shownError = codeComplete ? errors.verification?.message : undefined;
    // Stops a second VERIFY press or Enter sending the code again while the
    // first send is in flight. isSubmitting updates a render too late for that.
    const submitOnceRef = useRef(false);

    // DIS-474: Reset form state when screen loses focus to prevent stale OTP codes
    // persisting between visits and breaking automation tests
    useFocusEffect(
      React.useCallback(() => {
        // Cleanup function runs when screen blurs
        return () => {
          setResending(false);
          setCodeResent(false);
          setResendError(undefined);
          reset({verification: ''});
          ++resendReqIdRef.current;
        };
      }, [reset]),
    );

    const onSubmit = async (values: OtpVerificationFormData) => {
      if (submitOnceRef.current) return;
      submitOnceRef.current = true;
      try {
        await sendCode(values);
      } finally {
        submitOnceRef.current = false;
      }
    };

    const sendCode = async (values: OtpVerificationFormData) => {
      const selectedStore = shopSelectionStore.selectedStoreSummary;

      if (!selectedStore) {
        setError('verification', {type: 'manual', message: 'No selected store'});
        return;
      }

      const optInResponse = await apiService.rewardsOptIn({
        phoneNumber,
        verificationCode: values.verification,
        dateOfBirth: userStore.dateOfBirth,
        emailAddress: userStore.emailAddress,
        firstName: userStore.firstName,
        lastName: userStore.lastName,
        selectedStore: selectedStore,
      });

      if (optInResponse) {
        await rewardsStore.loadRewardsForUser(phoneNumber);

        // reload the cart to apply rewards pricing to existing cart items
        cartStore.fullyReloadOrderFromMFL();

        goToActivateSuccess(navigation, {userStore, shopSelectionStore}); // Success page
      } else {
        setError('verification', {
          type: 'manual',
          message: 'Wrong code entered. Please try again.',
        });
      }
    };

    const resendCode = () => async () => {
      const reqId = ++resendReqIdRef.current;
      reset({verification: ''});
      setResendError(undefined);
      setResending(true);

      // apiService can stall so set timeout here
      const resendTimeout = setTimeout(() => {
        if (reqId !== resendReqIdRef.current) return;
        ++resendReqIdRef.current;
        setResending(false);
        setCodeResent(false);
        setResendError(RESEND_ERROR_MESSAGE);
      }, TIMEOUT);

      try {
        // resend code can use rewardsVerifyExistingUserPhoneNumber
        // for existing and new user. there are no side effects
        const success = await slowDown(
          apiService
            .rewardsVerifyExistingUserPhoneNumber({
              phoneNumber: userStore.phoneNumber,
            })
            .then(result => result?.status === 200),
        );
        if (reqId !== resendReqIdRef.current) return;

        setCodeResent(success);
        setResendError(success ? undefined : RESEND_ERROR_MESSAGE);
      } catch (_e) {
        if (reqId !== resendReqIdRef.current) return;
        setCodeResent(false);
        setResendError(RESEND_ERROR_MESSAGE);
      } finally {
        clearTimeout(resendTimeout);
        if (reqId === resendReqIdRef.current) {
          setResending(false);
        }
      }
    };

    const {isDesktopCard: isWide, onLayout} = useWideLayout();

    // DIS-2006: the shared new-design phone verification view, as the join
    // flow's RewardsVerificationScreen renders it. This screen keeps its own
    // opt-in send and resend logic. DIS-1824: the code is sent only by VERIFY
    // or Enter, never when the sixth digit is typed.
    const verificationView = (
      <PhoneVerificationView
        title="VERIFY YOUR MOBILE"
        intro={`Enter the 6-digit code we sent to\n${formatMobileForDisplay(phoneNumber)}`}
        notice={codeResent ? 'We have re-sent your code to your mobile.' : undefined}
        otp={
          <Controller
            control={control}
            name="verification"
            render={({field: {onChange, onBlur, value}}) => (
              <Otp
                value={value}
                numInputs={6}
                onChangeText={onChange}
                onEnter={handleSubmit(onSubmit)}
                onBlur={onBlur}
                error={!!shownError}
                boxSize={isWide ? otpBoxSize : undefined}
              />
            )}
          />
        }
        error={shownError}
        verifyDisabled={!codeComplete || isSubmitting}
        verifying={isSubmitting}
        onVerify={handleSubmit(onSubmit)}
        resend={resending ? 'sending' : 'ready'}
        resendError={resendError}
        onResend={resendCode()}
        onChangeNumber={() => {
          // Navigate from ROOT_REWARDS_FLOW to ROOT > MAIN > ACCOUNT_DETAIL
          RootNavigation.navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {
              screen: AppRoute.ACCOUNT_DETAIL,
            },
          });
        }}
        onBack={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
          }
        }}
        // Desktop card (536:4455) on wide web only.
        centredOnLayout={isWide ? onLayout : undefined}
      />
    );

    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white', flex: 1}}
        contentContainerStyle={{flexGrow: 1}}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        {isWide ? (
          verificationView
        ) : (
          <ContentView
            parentStyle={{paddingBottom: space.s16}}
            style={{flex: 1}}
            onLayout={onLayout}>
            {verificationView}
          </ContentView>
        )}
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsActivateVerificationScreen;
